/**
 * Shared cap-resolution helpers for social-account integrations
 * (instagram / facebook / linkedin / youtube). MUST stay in lockstep with the
 * backend gates that read `entitlements.sub_features.integrations.<key>` —
 * a UX/enforcement skew (e.g. UI offering "Add another account" while the
 * backend 403s the next connect) is the bug class this guards against.
 *
 * Storage convention on the entitlement value:
 *   - undefined / null  → unlimited (default-allow for legacy plans)
 *   - 0                 → integration disabled
 *   - -1                → unlimited (explicit)
 *   - n ≥ 1             → numeric cap
 *
 * `1` USED TO MEAN UNLIMITED and no longer does. It was the pre-cap-UI
 * "enabled" sentinel, and this file kept translating it to `unlimited` long
 * after the backend stopped: migration
 * `drizzle/0168_migrate_legacy_social_cap_sentinel.sql` (2026-05-14) flipped
 * every legacy 1 to -1, and the same deploy removed the `cap === 1 → allow`
 * clause from InstagramOAuthService / FacebookService / LinkedInService /
 * YouTubeOAuthService. A stored 1 today is an admin's explicit "one account".
 * Reading it as unlimited told a capped org it could add another account and
 * then let the backend refuse — exactly the skew above. Do NOT re-add the
 * sentinel branch here without reverting all four services with it.
 */
export type SocialAccountCap =
  | { state: 'unlimited' }
  | { state: 'disabled' }
  | { state: 'capped'; max: number };

export function resolveSocialAccountCap(value: number | undefined | null): SocialAccountCap {
  if (value === undefined || value === null || value === -1) {
    return { state: 'unlimited' };
  }
  if (value === 0) {
    return { state: 'disabled' };
  }
  return { state: 'capped', max: value };
}

/**
 * Coerce a raw `sub_features.integrations.<key>` value to the number
 * `resolveSocialAccountCap` expects, or `undefined` when the plan says nothing.
 *
 * The value does NOT always arrive as a number, which is why this exists:
 *   - plan path        → `subConfig.limit ?? 0`, a number (EntitlementService)
 *   - per-org override → whatever `JSON.parse` produced; `limit_value` is a
 *                        VARCHAR, so a numeric STRING is routine
 *   - minimal / no-subscription branch → a BOOLEAN
 *                        (`sub_features: { integrations: { uber_direct: true } }`)
 *   - legacy rows      → the full `{ enabled, limit }` object
 *
 * Mirrors `numFromVal` in EntitlementService._syncLegacyMenuFeatureKeys and the
 * inline parse in SocialMediaPostsManager.isPlatformAllowed, so every surface
 * reads one stored value the same way. `undefined` is returned for anything
 * unrecognised — the CALLER decides whether "the plan said nothing" means allow
 * (back-compat) or deny, because that answer differs per surface.
 *
 * NOTE the object branch reads `limit` and deliberately IGNORES `enabled`: that
 * is the convention the backend already persists with (`subConfig.limit ?? 0`),
 * so honouring `enabled` here would make the frontend stricter than the value
 * every backend gate actually enforces.
 */
export function normalizeSocialCapValue(raw: unknown): number | undefined {
  if (raw === undefined || raw === null) return undefined;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : undefined;
  // A bare `true` is the minimal-entitlements shape for "granted, uncapped";
  // `false` is an explicit disable.
  if (typeof raw === 'boolean') return raw ? -1 : 0;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed === '') return undefined;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : undefined;
  }
  if (typeof raw === 'object' && 'limit' in (raw as Record<string, unknown>)) {
    return normalizeSocialCapValue((raw as { limit: unknown }).limit);
  }
  return undefined;
}

/**
 * The cap turned into the four things a connect surface actually needs. Kept
 * here rather than at each call site so "may another account be connected?" is
 * answered once — the account pages, the integrations drawer and anything
 * added later cannot drift on the `used >= max` comparison or on the wording.
 */
export interface SocialAccountSlots {
  cap: SocialAccountCap;
  used: number;
  /** Plan switched this platform off entirely. */
  disabled: boolean;
  /** Plan allows the platform, but every slot is taken. */
  atCap: boolean;
  /** May another account be connected right now? */
  canConnect: boolean;
  /**
   * Why connecting is blocked, ready to render. null when it is not blocked.
   *
   * Only worth showing where the absence of a connect button would otherwise be
   * inexplicable — i.e. a platform switched OFF on the plan, whose panel would
   * read "nothing connected" with no way forward. A surface that already lists
   * connected accounts should just drop the button at the cap and say nothing:
   * having used every slot you were granted is a correct state, not a fault to
   * report back every time the panel opens.
   */
  blockedReason: string | null;
}

export function resolveSocialAccountSlots({
  value,
  used,
  platformName,
}: {
  /** Raw `sub_features.integrations.<key>` value. */
  value: number | undefined | null;
  /** How many accounts are connected today. */
  used: number;
  /** "Instagram" — used in the blocked wording. */
  platformName: string;
}): SocialAccountSlots {
  const cap = resolveSocialAccountCap(value);
  // Defensive: a negative or non-finite count must never read as free capacity.
  const safeUsed = Number.isFinite(used) && used > 0 ? Math.floor(used) : 0;
  const disabled = cap.state === 'disabled';
  const atCap = cap.state === 'capped' && safeUsed >= cap.max;

  return {
    cap,
    used: safeUsed,
    disabled,
    atCap,
    canConnect: !disabled && !atCap,
    blockedReason: disabled ? `${platformName} is not available on your current plan.` : null,
  };
}
